import React, { useCallback, useState, useEffect } from "react";
import { Box } from "@material-ui/core";
import {
  usePermission,
  useSelectedTheme,
  useTranslate,
} from "../../../../../Hooks";
import {
  GetAllScopeDocuments,
  GetUnitDocumentsByContactId,
} from "../../../../../Services";
import { useVerticalNav } from "../../../../../Contexts/VerticalNavContext";
import { ScopeDocumentEnum } from "../../../../../Enums";
import { StaticLookupsIds } from "../../../../../assets/json/StaticLookupsIds";
import { ContactUploadDocument } from "../../../NewContactsView/ContactOverview/UI";
import { DocumentsUnitCard } from "./UI";
import {
  CustomButton,
  CustomDialog,
  CustomViewModeSwitcher,
  DocumentsContactCard,
  DocumentsContactCardSkeleton,
  NoDataFound,
} from "../../../../../Components";
import ContactShareDocumentsDialogContent from "../../../NewContactsView/ContactOverview/UI/ContactDocuments/UI/ContactShareDocumentsDialogContent";

// icons
import { Building04, PlusIcon, User03Icon } from "../../../../../assets/icons";

// styles
import useStyles from "./styles";
import { AfterSalePermissions } from "../../../../../Permissions";
import { havePermissionToViewDetails } from "../../../../../Helper";

const VIEW_MODES = {
  UNIT: "unit",
  CONTACT: "contact",
};

const DocumentsPanel = ({ contactId }) => {
  const styles = useStyles();
  const { translate } = useTranslate("AfterSalesClientProfileView");
  const { setAlertBoxContent } = useVerticalNav();
  const {
    theme: { palette },
  } = useSelectedTheme();
  const { isAllowed } = usePermission(Object.values(AfterSalePermissions), [
    AfterSalePermissions.UploadDocument.permissionsId,
  ]);
  const [viewMode, setViewMode] = useState(VIEW_MODES.CONTACT);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenAddNewDocumentDialog, setIsOpenAddNewDocumentDialog] =
    useState(false);
  const [updatedGetAllScopeDocuments, setUpdatedGetAllScopeDocuments] =
    useState(false);
  const [isShareDocumentsDialogOpen, setIsShareDocumentsDialogOpen] =
    useState(false);
  const [selectedDocumentToShare, setSelectedDocumentToShare] = useState(null);
  const [isShareConfirmed, setIsShareConfirmed] = useState(false);
  const [contactDocuments, setContactDocuments] = useState({
    result: [],
    totalCount: 0,
  });
  const [unitDocuments, setUnitDocuments] = useState({ result: [] });
  const [pagination, setPagination] = useState({
    pageSize: 100,
    pageIndex: 1,
  });

  const MODES = [
    {
      value: "unit",
      icon: (
        <Building04 width={20} height={20} fill={palette.button.secondary_fg} />
      ),
      label: translate("unit"),
    },
    {
      value: "contact",
      icon: (
        <User03Icon width={20} height={20} fill={palette.button.secondary_fg} />
      ),
      label: translate("contact"),
    },
  ];

  const getAllDocuments = useCallback(async () => {
    setIsLoading(true);
    try {
      const body = {
        scopeId: contactId,
        pageSize: pagination.pageSize,
        pageIndex: pagination.pageIndex - 1,
        scopeTypeId: ScopeDocumentEnum.Contact.scopeTypeId,
      };
      const res = await GetAllScopeDocuments(body);

      if (!(res.status && res.status !== 200))
        setContactDocuments({
          result: res.result,
          totalCount: res.totalCount,
        });
    } catch (error) {
      setContactDocuments({
        result: [],
        totalCount: 0,
      });
      setAlertBoxContent({
        display: true,
        variant: "error",
        message: translate("Something-went-wrong"),
        onClose: () => setAlertBoxContent(null),
      });
      console.error("Error fetching documents:", error);
    } finally {
      setIsLoading(false);
      setUpdatedGetAllScopeDocuments(false);
    }
  }, []);

  const GetAllUnitDocumentsByContactId = useCallback(async () => {
    setIsLoading(true);

    try {
      const res = await GetUnitDocumentsByContactId(contactId);

      if (!(res.status && res.status !== 200))
        setUnitDocuments({ result: res });
    } catch (error) {
      setUnitDocuments({ result: [] });
      setAlertBoxContent({
        display: true,
        variant: "error",
        message: translate("Something-went-wrong"),
        onClose: () => setAlertBoxContent(null),
      });
      console.error("Error fetching unit documents:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleAddDocumentContact = () => {
    setIsOpenAddNewDocumentDialog(true);
  };

  const onChangeViewMode = (value) => {
    setViewMode(value);
  };

  const onShareClickHandler = (document) => {
    setIsShareDocumentsDialogOpen(true);
    setSelectedDocumentToShare(document);
  };

  const onProceedClicked = () => {
    setIsShareConfirmed(true);
  };

  useEffect(() => {
    if (contactId) {
      if (viewMode === VIEW_MODES.CONTACT) {
        getAllDocuments();
      } else {
        GetAllUnitDocumentsByContactId();
      }
    }
  }, [contactId, viewMode]);

  useEffect(() => {
    if (updatedGetAllScopeDocuments) {
      getAllDocuments();
    }
  }, [updatedGetAllScopeDocuments]);

  return (
    <Box>
      <Box
        className={styles.switcherBox}
        display="flex"
        justifyContent={
          viewMode === VIEW_MODES.CONTACT ? "space-between" : "flex-end"
        }
      >
        {viewMode === VIEW_MODES.CONTACT && isAllowed ? (
          <CustomButton
            boxShadow="xs"
            size="lg"
            variant="outlined"
            style={{ maxWidth: "fit-content" }}
            color="secondary"
            startIcon={
              <PlusIcon
                width={20}
                height={20}
                fill={palette.button.secondary_fg}
              />
            }
            onClick={() => handleAddDocumentContact()}
          >
            {translate("upload-document")}
          </CustomButton>
        ) : (
          <Box style={{ width: 180 }} />
        )}
        <CustomViewModeSwitcher
          modes={MODES}
          selectedMode={viewMode}
          onChangeMode={onChangeViewMode}
          isLoading={isLoading}
        />
      </Box>
      {viewMode === VIEW_MODES.CONTACT && (
        <>
          <Box className={styles.gridContainer}>
            {isLoading ? (
              <DocumentsContactCardSkeleton CardSkeletonNumber={6} />
            ) : contactDocuments?.result?.length > 0 ? (
              contactDocuments?.result?.map((document) => (
                <DocumentsContactCard
                  key={document}
                  documentItem={document}
                  onShareClickHandler={onShareClickHandler}
                  setUpdatedGetAllScopeDocuments={
                    setUpdatedGetAllScopeDocuments
                  }
                  documents={contactDocuments}
                />
              ))
            ) : (
              <NoDataFound title={translate("no-documents-found")} />
            )}
          </Box>
          <ContactUploadDocument
            open={isOpenAddNewDocumentDialog}
            close={() => {
              setIsOpenAddNewDocumentDialog(false);
            }}
            lookupTypeId={StaticLookupsIds.ContactUploadDocumentsType}
            translate={translate}
            contactId={contactId}
            setUpdatedGetAllScopeDocuments={setUpdatedGetAllScopeDocuments}
          />
          {isShareDocumentsDialogOpen && (
            <CustomDialog
              open={true}
              width="688px"
              title={translate("share-document")}
              subtitle={translate("share-document-dialog-sub-title")}
              confirmText={translate("proceed")}
              onConfirm={() => onProceedClicked()}
              cancelText={translate("cancel")}
              onClose={() => setIsShareDocumentsDialogOpen(false)}
              isDisabled={false}
              content={
                <ContactShareDocumentsDialogContent
                  translate={translate}
                  document={selectedDocumentToShare}
                  isShareConfirmed={isShareConfirmed}
                  setIsShareConfirmed={setIsShareConfirmed}
                  setIsShareDocumentsDialogOpen={setIsShareDocumentsDialogOpen}
                />
              }
            />
          )}
        </>
      )}
      {viewMode === VIEW_MODES.UNIT && (
        <Box className={styles.gridContainer}>
          {isLoading ? (
            <DocumentsContactCardSkeleton CardSkeletonNumber={6} />
          ) : unitDocuments?.result.length > 0 ? (
            unitDocuments?.result.map((document) => (
              <DocumentsUnitCard key={document} documentItem={document} />
            ))
          ) : (
            <NoDataFound title={translate("no-documents-found")} />
          )}
        </Box>
      )}
    </Box>
  );
};

export default DocumentsPanel;
